import tippy from 'tippy.js/headless';
import debounce from 'lodash/debounce';
import axios from 'axios';
import domReady from '../helpers/dom-ready';
import checkUser from '../helpers/check-user';

class Menu {
  static init() {
    return new Menu();
  }

  constructor() {
    this.menu = document.querySelector('.brix-menu');
    this.main = document.querySelector('main');

    this.isMenuOpen = false;
    this.isMenuClosing = false;
    this.isSearchFormOpen = false;

    this.userIcon = this.menu.querySelector('.user');
    this.cartIcon = this.menu.querySelector('.cart');
    this.myAccountLink = this.menu.querySelector('.my-account-link');
    this.logOutLink = this.menu.querySelector('.logout-link');

    this.setUpMenus();
    this.setupPopperPlugin();
    this.setupTopMenuLanguageSwitcher();
    this.setupMainMenuLanguageSwitcher();
    this.setupSearch();
    this.setupNativeLanguageSwitcher();
    this.checkUser();
  }

  setUpMenus() {
    this.hamburgerButton = this.menu.querySelector('.menu-button');
    this.menuWrapper = this.menu.querySelector('.menu-wrapper');
    this.primaryMenu = this.menu.querySelector('.primary-menu');
    const topMenuItems = this.menu.querySelectorAll('.top-menu-item.-has-sub-menu');

    this.toggleScrolled();

    window.addEventListener('resize', debounce(() => {
      if (this.isSearchFormOpen && window.innerWidth >= 1208) {
        this.closeSearchForm();
      }
    }), 100);

    window.addEventListener('scroll', debounce(() => {
      this.toggleScrolled();
    }), 100);

    this.hamburgerButton.addEventListener('click', () => {
      if (!this.isMenuOpen) {
        this.openMenu();
      } else {
        this.closeMenu();
      }
    });

    this.menuWrapper.addEventListener('click', (e) => {
      if (this.isMenuOpen) {
        if (e.target !== e.currentTarget) return;
        this.closeMenu();
      }
    });

    this.primaryMenu.addEventListener('transitionend', () => {
      if (!this.isMenuOpen) {
        this.closeSubMenus();
      }
    });

    this.hamburgerButton.addEventListener('animationend', () => {
      if (this.isMenuClosing) {
        this.hamburgerButton.classList.remove('-closing');
        this.isMenuClosing = false;
      }
    });

    const primaryMenuItems = this.menu.querySelectorAll('.primary-menu-item');
    const backButtons = this.menu.querySelectorAll('.back-button');
    this.subMenus = this.menu.querySelectorAll('.sub-menu');

    [...primaryMenuItems].forEach((button) => {
      button.addEventListener('click', () => {
        const { id } = button.dataset;
        const subMenu = document.querySelector(`.sub-menu[data-id='${id}']`);
        subMenu.classList.add('-open');
        this.primaryMenu.classList.add('-no-scroll');
      });
    });

    [...backButtons].forEach((button) => {
      button.addEventListener('click', () => {
        [...this.subMenus].forEach((subMenu) => {
          subMenu.classList.remove('-open');
          this.primaryMenu.classList.remove('-no-scroll');
        });
      });
    });

    [...topMenuItems].forEach((menuItem) => {
      menuItem.addEventListener('click', () => {
        const { id } = menuItem.dataset;
        const subMenu = document.querySelector(`.sub-menu[data-id='${id}']`);
        subMenu.classList.add('-open');
        this.openMenu();
      });
    });
  }

  openMenu() {
    this.hamburgerButton.classList.add('-open');
    this.menuWrapper.classList.add('-open');
    this.disableBodyScroll();
    this.isMenuOpen = true;

    if (this.isSearchFormOpen) {
      this.searchOverlay.classList.add('-hidden');
      this.searchInput.blur();
      this.isSearchFormOpen = false;
    }
  }

  closeSubMenus() {
    [...this.subMenus].forEach((subMenu) => {
      subMenu.classList.remove('-open');
    });
  }

  closeMenu() {
    this.isMenuClosing = true;
    this.hamburgerButton.classList.remove('-open');
    this.menuWrapper.classList.remove('-open');
    this.hamburgerButton.classList.add('-closing');
    this.isMenuOpen = false;
    this.enableBodyScroll();
  }

  toggleScrolled() {
    if (window.scrollY > 48) {
      this.menu.classList.add('-scrolled');
    } else if (!this.isSearchFormOpen) {
      this.menu.classList.remove('-scrolled');
    }
  }

  setupSearch() {
    const showSearchButton = this.menu.querySelector('.show-search-button');
    this.searchOverlay = this.menu.querySelector('.search-overlay');
    const searchForm = this.menu.querySelector('.search-form');
    const cancelSearchButton = this.menu.querySelector('.search-cancel');
    const clearSearchButton = this.menu.querySelector('.search-clear');
    this.searchInput = searchForm.querySelector('.search-input');

    if (window.innerWidth > 1208) {
      if (this.searchInput.value !== '') {
        clearSearchButton.classList.remove('-hidden');
        cancelSearchButton.classList.add('-hidden');
      }
    }

    this.searchOverlay.addEventListener('click', (e) => {
      if (this.isSearchFormOpen) {
        if (e.target !== e.currentTarget) return;
        this.closeSearchForm();
      }
    });

    showSearchButton.addEventListener('click', () => {
      if (window.innerWidth <= 1208) {
        if (!this.isSearchFormOpen) {
          this.menu.classList.add('-scrolled');
          this.searchOverlay.classList.remove('-hidden');
          this.searchInput.focus();
          this.main.classList.add('-overlay');
          this.isSearchFormOpen = true;
          this.disableBodyScroll();
        }
      } else {
        searchForm.submit();
      }
    });

    this.searchInput.addEventListener('input', () => {
      if (this.searchInput.value !== '') {
        cancelSearchButton.classList.add('-hidden');
        clearSearchButton.classList.remove('-hidden');
      } else {
        cancelSearchButton.classList.remove('-hidden');
        clearSearchButton.classList.add('-hidden');
      }
    });

    cancelSearchButton.addEventListener('click', (e) => {
      e.preventDefault();
      if (this.isSearchFormOpen) {
        this.closeSearchForm();
      }
    });

    clearSearchButton.addEventListener('click', (e) => {
      e.preventDefault();
      this.searchInput.value = '';
      cancelSearchButton.classList.remove('-hidden');
      clearSearchButton.classList.add('-hidden');
      this.searchInput.focus();
    });

    this.searchInput.addEventListener('focus', () => {
      if (this.searchInput.value !== '') {
        clearSearchButton.classList.remove('-hidden');
        cancelSearchButton.classList.add('-hidden');
      }
    });

    this.searchInput.addEventListener('focusout', () => {
      if (window.innerWidth >= 1208) {
        setTimeout(() => {
          clearSearchButton.classList.add('-hidden');
        }, 100);
      }
    });

    searchForm.addEventListener('submit', (e) => {
      e.preventDefault();

      if (this.searchInput.value === '') {
        this.searchInput.focus();
      } else {
        const { searchUrl } = searchForm.dataset;
        const searchQuery = encodeURIComponent(this.searchInput.value).replace(/%20/g, '+');

        window.location.href = `${searchUrl}?search=${searchQuery}`;
      }
    });
  }

  closeSearchForm() {
    this.searchOverlay.classList.add('-hidden');
    this.searchInput.blur();
    this.main.classList.remove('-overlay');
    this.enableBodyScroll();
    this.isSearchFormOpen = false;

    this.toggleScrolled();
  }

  setupPopperPlugin() {
    this.hideOnPopperBlur = {
      name: 'hideOnPopperBlur',
      defaultValue: true,
      fn(instance) {
        return {
          onCreate() {
            instance.popper.addEventListener('focusout', (event) => {
              if (
                instance.props.hideOnPopperBlur
                  && event.relatedTarget
                  && !instance.popper.contains(event.relatedTarget)
              ) {
                instance.hide();
              }
            });
          },
        };
      },
    };
  }

  setupTopMenuLanguageSwitcher() {
    const topMenuLanguageSwitcher = this.menu.querySelector('.top-menu-language-switcher');
    if (!topMenuLanguageSwitcher) { return; }
    const switcherButton = topMenuLanguageSwitcher.querySelector('.switcher-button');
    const languages = topMenuLanguageSwitcher.querySelector('.languages');
    languages.style.minWidth = `${switcherButton.offsetWidth}px`;
    const languageButtons = languages.querySelectorAll('button');
    [...languageButtons].forEach((button) => {
      button.addEventListener('click', () => {
        window.location.href = button.dataset.url;
      });
    });

    tippy(switcherButton, {
      content: languages,
      offset: [0, 4],
      allowHTML: true,
      trigger: 'click',
      appendTo: 'parent',
      interactive: 'true',
      placement: 'bottom-start',
      plugins: [this.hideOnPopperBlur],

      render(instance) {
        const popper = document.createElement('div');
        popper.appendChild(instance.props.content);
        return { popper };
      },
    });
  }

  setupNativeLanguageSwitcher() {
    const nativeLanguageSwitcher = this.menu.querySelector('.native-language-switcher');
    if (!nativeLanguageSwitcher) { return; }
    const languageOptions = nativeLanguageSwitcher.querySelectorAll('option');

    nativeLanguageSwitcher.addEventListener('change', (e) => {
      const button = [...languageOptions].find((option) => option.dataset.slug === e.target.value);
      window.location.href = button.dataset.url;
    });
  }

  setupMainMenuLanguageSwitcher() {
    const mainMenuLanguageSwitcher = this.menu.querySelector('.main-menu-language-switcher');
    if (!mainMenuLanguageSwitcher) { return; }
    const switcherButton = mainMenuLanguageSwitcher.querySelector('.switcher-button');
    const languages = mainMenuLanguageSwitcher.querySelector('.languages');
    languages.style.minWidth = `${switcherButton.offsetWidth}px`;
    const languageButtons = languages.querySelectorAll('button');
    [...languageButtons].forEach((button) => {
      button.addEventListener('click', () => {
        window.location.href = button.dataset.url;
      });
    });

    tippy(switcherButton, {
      content: languages,
      offset: [0, 4],
      allowHTML: true,
      trigger: 'click',
      appendTo: 'parent',
      interactive: 'true',
      placement: 'bottom-start',
      plugins: [this.hideOnPopperBlur],

      render(instance) {
        const popper = document.createElement('div');
        popper.appendChild(instance.props.content);
        return { popper };
      },
    });
  }

  enableBodyScroll() {
    document.body.classList.remove('-scroll-lock');
  }

  disableBodyScroll() {
    document.body.classList.add('-scroll-lock');
  }

  checkUser() {
    const restUrl = document.querySelector('meta[name="rest-url"]').getAttribute('content');

    checkUser(
      () => {
        this.cartIcon.classList.remove('-hidden');
        this.userIcon.classList.add('-hidden');
        this.logOutLink.classList.remove('-hidden');

        this.myAccountLink.innerHTML = this.myAccountLink.dataset.loggedInText;

        const cartCount = this.cartIcon.querySelector('.cart-count');
        axios.get(`${restUrl}wc/store/cart`)
          .then((cartResponse) => {
            if (cartResponse.data) {
              if (cartResponse.data.items_count > 0) {
                cartCount.classList.remove('-hidden');
                cartCount.innerText = cartResponse.data.items_count;
              }
            }
          })
          .catch((e) => {
            console.log(`Error: ${e}`);
          });
      },
      () => {
        this.cartIcon.classList.add('-hidden');
        this.userIcon.classList.remove('-hidden');
        this.myAccountLink.innerHTML = this.myAccountLink.dataset.loggedOutText;
      },
    );
  }
}

domReady(() => {
  Menu.init();
});
